import { useQuery } from '@apollo/client'
import { GET_ZOOM_PASSWORDS } from '@landscape/gql'
import { FILE_TYPES, searchBarFocusState } from '@landscape/ui-lib'
import { InstitutionInfo } from 'components/etc/InstitutionInfo'
import { setPageCode } from 'contextUtil'
import { get } from 'lodash'
import React, { useEffect, useLayoutEffect } from 'react'
import { useSetRecoilState } from 'recoil'
import styled from 'styled-components'
import { FileDownloadLink } from '../file'

const MetaList = styled.div`
  span.meta-item {
    font-size: 0.75rem;
    line-height: 1.5em;
    font-weight: 600;
    font-family: Roboto Bold, sans-serif;

    &:not(:last-child)::after {
      content: '\\7c';
      color: #b2b2b2;
      padding: 0 6px;
    }
  }
`

const InfoSection = styled.div`
  text-align: left;
  margin-bottom: 40px;

  & a {
    color: #2903ff;
  }
`

const ResourceItem = styled.div`
  margin: 20px 0px;

  & button {
    padding-left: 0px !important;
    color: #0077c8;
  }
`

const VideoDownloadLink = styled.a`
  color: #0077c8 !important;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`

const renderZoomPassword = (password?: string) => {
  if (!password) return null

  return (
    <span className="meta-item cb-red1-color" style={{ textTransform: 'none' }}>
      Password To View Video - {password}
    </span>
  )
}

const Help: React.FC = () => {
  const setSearchBarFocus = useSetRecoilState(searchBarFocusState)

  const { data } = useQuery(GET_ZOOM_PASSWORDS, {
    fetchPolicy: 'cache-and-network',
  })

  const zoomPasswords = get(data, 'getZoomPasswords.values')

  useLayoutEffect(() => {
    document.title = 'Landscape - Help'
    setPageCode('help')
  }, [])

  useEffect(() => setSearchBarFocus(false), [setSearchBarFocus])
  return (
    <>
      <InstitutionInfo />

      <div className="cb-padding-bottom-48" id="main-content">
        <InfoSection>
          <h4 className="cb-roboto-slab-bold cb-margin-bottom-8">
            Technical Documentation for Uploading/Downloading Files
          </h4>
          <p>
            The attached documentation provides information on the data elements, file layout, and
            instructions on how to upload and download files. Files can be uploaded as often as
            needed. Returned, amended files, contain record level context data as well as a unique
            url per record that directs to that applicant’s Dashboard.
          </p>
          <ResourceItem>
            <FileDownloadLink
              type={FILE_TYPES.DOC}
              originalFileName={'Landscape File Template.csv'}
              ariaLabel="Download Landscape Upload File CSV Template"
              ariaDescribedBy="landscape_file_template"
            />
            <MetaList id="landscape_file_template">
              <span className="meta-item">Excel Spreadsheet</span>
              <span className="meta-item">460 Bytes</span>
              <span className="meta-item">
                <span
                  className="cb-glyph cb-educator"
                  data-alt="For Educators"
                  aria-label="For Educators"
                  aria-hidden="true"
                ></span>
              </span>
            </MetaList>
          </ResourceItem>
          <ResourceItem>
            <FileDownloadLink
              type={FILE_TYPES.DOC}
              originalFileName={'Landscape Technical Guide.pdf'}
              ariaLabel="Download Landscape Technical Guide"
              ariaDescribedBy="landscape_technical_guide"
            />
            <MetaList id="landscape_technical_guide">
              <span className="meta-item">PDF</span>
              <span className="meta-item">275 KB</span>
              <span className="meta-item">
                <span
                  className="cb-glyph cb-educator"
                  data-alt="For Educators"
                  aria-label="For Educators"
                  aria-hidden="true"
                ></span>
              </span>
            </MetaList>
          </ResourceItem>
          <ResourceItem>
            <FileDownloadLink
              type={FILE_TYPES.DOC}
              originalFileName={'Landscape Tagged Data Definitions.pdf'}
              ariaLabel="Download a list defining data fields in the processed Landscape data file"
              ariaDescribedBy="landscape_data_definitions"
            />
            <MetaList id="landscape_data_definitions">
              <span className="meta-item">PDF</span>
              <span className="meta-item">114.1 KB</span>
              <span className="meta-item">
                A list defining data fields in the processed Landscape data file. This file is
                created from your uploaded data file and is available for download on the Jobs page.
              </span>
              <span className="meta-item">
                <span
                  className="cb-glyph cb-educator"
                  data-alt="For Educators"
                  aria-label="For Educators"
                  aria-hidden="true"
                ></span>
              </span>
            </MetaList>
          </ResourceItem>
        </InfoSection>

        <InfoSection>
          <h4 className="cb-roboto-slab-bold cb-margin-bottom-8"> Training Materials </h4>
          <p>
            The College Board has produced the following strategic resources and materials to help
            guide training on campus:
          </p>
          <ResourceItem>
            <FileDownloadLink
              type={FILE_TYPES.DOC}
              originalFileName={'Landscape Admissions Leaders Guide.pdf'}
              ariaLabel="Download Landscape Admissions Leaders Guide"
              ariaDescribedBy="landscape_admissions_leaders_guide"
            />
            <MetaList id="landscape_admissions_leaders_guide">
              <span className="meta-item">PDF</span>
              <span className="meta-item">152.7 KB</span>
              <span className="meta-item">
                An overview for directors of admissions containing key foundational information,
                strategic considerations to guide usage, and an overview of the resource and the
                training materials.
              </span>
              <span className="meta-item">
                <span
                  className="cb-glyph cb-educator"
                  data-alt="For Educators"
                  aria-label="For Educators"
                  aria-hidden="true"
                ></span>
              </span>
            </MetaList>
          </ResourceItem>
          <ResourceItem>
            <FileDownloadLink
              type={FILE_TYPES.DOC}
              originalFileName={'Landscape Use Cases and Best Practices.pdf'}
              ariaLabel="Download Landscape Use Cases and Best Practices"
              ariaDescribedBy="landscape_best_pratices"
            />
            <MetaList id="landscape_best_pratices">
              <span className="meta-item">PDF</span>
              <span className="meta-item">337.5 KB</span>
              <span className="meta-item">
                A strategic document for leaders that summarizes findings and ways that institutions
                incorporated the tool in the first years of the pilot program
              </span>
              <span className="meta-item">
                <span
                  className="cb-glyph cb-educator"
                  data-alt="For Educators"
                  aria-label="For Educators"
                  aria-hidden="true"
                ></span>
              </span>
            </MetaList>
          </ResourceItem>
          <ResourceItem>
            <FileDownloadLink
              type={FILE_TYPES.DOC}
              originalFileName={'Landscape Research Overview.pdf'}
              ariaLabel="Download Landscape Research Overview document"
              ariaDescribedBy="landscape_research_overview"
            />
            <MetaList id="landscape_research_overview">
              <span className="meta-item">PDF</span>
              <span className="meta-item">225.5 KB</span>
              <span className="meta-item">
                A high-level summary of key research related to the use of context in the admissions
                process.
              </span>
              <span className="meta-item">
                <span
                  className="cb-glyph cb-educator"
                  data-alt="For Educators"
                  aria-label="For Educators"
                  aria-hidden="true"
                ></span>
              </span>
            </MetaList>
          </ResourceItem>
          <ResourceItem>
            <FileDownloadLink
              type={FILE_TYPES.DOC}
              originalFileName={'Landscape Annotated Training Deck 2023.pptx'}
              ariaLabel="Download Landscape Annotated Training Deck 2023 powerpoint document"
              ariaDescribedBy="landscape_training_deck_2023"
            />
            <MetaList id="landscape_training_deck_2023">
              <span className="meta-item">Powerpoint</span>
              <span className="meta-item">6.6 MB</span>
              <span className="meta-item">
                An annotated PowerPoint deck designed to be used by admissions leaders and others
                tasked with training on campus to train staff and readers. It gives a grounding in
                the research, a walkthrough of Landscape’s dashboard, and a sense of why context is
                important.
              </span>
              <span className="meta-item">
                <span
                  className="cb-glyph cb-educator"
                  data-alt="For Educators"
                  aria-label="For Educators"
                  aria-hidden="true"
                ></span>
              </span>
            </MetaList>
          </ResourceItem>
          <ResourceItem>
            <FileDownloadLink
              type={FILE_TYPES.DOC}
              originalFileName={'Landscape Guide to Getting Started.pdf'}
              ariaLabel="Download Landscape Guide to Getting Started pdf document"
              ariaDescribedBy="landscape_getting_started"
            />
            <MetaList id="landscape_getting_started">
              <span className="meta-item">PDF</span>
              <span className="meta-item">183.1 KB</span>
              <span className="meta-item">
                This document gives admissions directors technical information important to the
                first-time implementation of Landscape on their campus.
              </span>
              <span className="meta-item">
                <span
                  className="cb-glyph cb-educator"
                  data-alt="For Educators"
                  aria-label="For Educators"
                  aria-hidden="true"
                ></span>
              </span>
            </MetaList>
          </ResourceItem>
          <ResourceItem>
            <FileDownloadLink
              type={FILE_TYPES.DOC}
              originalFileName={'Landscape User Walkthrough.pdf'}
              ariaLabel="Download Landscape User Walkthrough pdf document"
              ariaDescribedBy="landscape_user_walkthrough"
            />
            <MetaList id="landscape_user_walkthrough">
              <span className="meta-item">PDF</span>
              <span className="meta-item">365.9 KB</span>
              <span className="meta-item">
                This document gives a step-by-step overview of the various elements of the
                Dashboard. It is suitable for readers and admissions staff to use as a reference
                document as they review applications.
              </span>
              <span className="meta-item">
                <span
                  className="cb-glyph cb-educator"
                  data-alt="For Educators"
                  aria-label="For Educators"
                  aria-hidden="true"
                ></span>
              </span>
            </MetaList>
          </ResourceItem>
          <ResourceItem>
            <FileDownloadLink
              type={FILE_TYPES.DOC}
              originalFileName={'Landscape Data and Methodology Description.pdf'}
              ariaLabel="Download Landscape Data and Methodology Description pdf document"
              ariaDescribedBy="landscape_data_methodology"
            />
            <MetaList id="landscape_data_methodology">
              <span className="meta-item">PDF</span>
              <span className="meta-item">295.3 KB</span>
              <span className="meta-item">
                This data dictionary gives a detailed examination of the data elements of Landscape.
              </span>
              <span className="meta-item">
                <span
                  className="cb-glyph cb-educator"
                  data-alt="For Educators"
                  aria-label="For Educators"
                  aria-hidden="true"
                ></span>
              </span>
            </MetaList>
          </ResourceItem>
          <ResourceItem>
            <FileDownloadLink
              type={FILE_TYPES.DOC}
              originalFileName={
                'Landscape Case Study: Academic Programming at Florida State University.pptx'
              }
              ariaLabel="Landscape Case Study: Academic Programming at Florida State University powerpoint"
              ariaDescribedBy="landscape_case_study_academic_programming"
            />
            <MetaList id="landscape_case_study_academic_programming">
              <span className="meta-item">Powerpoint</span>
              <span className="meta-item">458.6 KB</span>
              <span className="meta-item">
                <span
                  className="cb-glyph cb-educator"
                  data-alt="For Educators"
                  aria-label="For Educators"
                  aria-hidden="true"
                ></span>
              </span>
            </MetaList>
          </ResourceItem>
          <ResourceItem>
            <FileDownloadLink
              type={FILE_TYPES.DOC}
              originalFileName={
                'Landscape Case Study: Improving Admissions Practice at Trinity University.pptx'
              }
              ariaLabel="Download Landscape Case Study: Improving Admissions Practice at Trinity University powerpoint"
              ariaDescribedBy="landscape_case_study_improving_admissions"
            />
            <MetaList id="landscape_case_study_improving_admissions">
              <span className="meta-item">Powerpoint</span>
              <span className="meta-item">456.1 KB</span>
              <span className="meta-item">
                <span
                  className="cb-glyph cb-educator"
                  data-alt="For Educators"
                  aria-label="For Educators"
                  aria-hidden="true"
                ></span>
              </span>
            </MetaList>
          </ResourceItem>
        </InfoSection>

        <InfoSection>
          <div>
            <h4 className="cb-roboto-slab-bold cb-margin-bottom-8"> Training Videos </h4>
            <ResourceItem>
              <FileDownloadLink
                type={FILE_TYPES.DOC}
                originalFileName={'Introduction to Landscape.mp4'}
                ariaLabel="Download Introduction to Landscape mp4 file"
                ariaDescribedBy="landscape_intro"
              />
              <MetaList id="landscape_intro">
                <span className="meta-item">MP4</span>
                <span className="meta-item">28.7 MB</span>
                <span className="meta-item">
                  An introduction of Landscape that defines what Landscape is, it's purpose, the
                  research behind its creation, how it should be used by college and university
                  admissions professionals and its impact.
                </span>
                <span className="meta-item">
                  <span
                    className="cb-glyph cb-educator"
                    data-alt="For Educators"
                    aria-label="For Educators"
                    aria-hidden="true"
                  ></span>
                </span>
              </MetaList>
            </ResourceItem>
            <ResourceItem>
              <FileDownloadLink
                type={FILE_TYPES.DOC}
                originalFileName={'A Walk-through of the Landscape Interface.mp4'}
                ariaLabel="Download A Walk-through of the Landscape Interface mp4 file"
                ariaDescribedBy="landscape_interface_walkthrough"
              />
              <MetaList id="landscape_interface_walkthrough">
                <span className="meta-item">MP4</span>
                <span className="meta-item">100.2 MB</span>
                <span className="meta-item">
                  A step-by-step view of the various pieces of Landscape and guidance and insight
                  into the data that informs the tool.
                </span>
                <span className="meta-item">
                  <span
                    className="cb-glyph cb-educator"
                    data-alt="For Educators"
                    aria-label="For Educators"
                    aria-hidden="true"
                  ></span>
                </span>
              </MetaList>
            </ResourceItem>
          </div>
          <br></br>

          <div>
            <h5 className="cb-roboto-slab-bold cb-margin-bottom-8">
              Training Your Staff to Use Landscape
            </h5>
            <p>
              College Board hosted webinar on how to train institutional admissions staff to use
              Landscape.
            </p>
            <ResourceItem>
              <VideoDownloadLink
                className="cb-link-default cb-link-blue cb-font-weight-bold"
                target="_blank"
                href="https://collegeboard.zoom.us/rec/share/_ehbJejQ_D9LZNaS60j5VKETH9jDaaa81CQZ-fYMnk7NsdLlpUJCuzW5oUEEl3DF"
                rel="noopener noreferrer"
                aria-describedby="landscape_practitioner_panel"
              >
                Practitioner Panel Session
              </VideoDownloadLink>
              <MetaList id="landscape_practitioner_panel">
                {renderZoomPassword(zoomPasswords?.landscape_practitioner_panel)}
                <span className="meta-item">Zoom Video Link</span>
                <span className="meta-item">
                  <span
                    className="cb-glyph cb-educator"
                    data-alt="For Educators"
                    aria-label="For Educators"
                    aria-hidden="true"
                  ></span>
                </span>
              </MetaList>
            </ResourceItem>
            <ResourceItem>
              <VideoDownloadLink
                className="cb-link-default cb-link-blue cb-font-weight-bold"
                target="_blank"
                href="https://collegeboard.zoom.us/rec/share/433KU-uleEHAyMgE22Rq90KcxYYKRIvsjTqmCt1KMkiFjWvWrHSVDa5G4kRksQSo.x9rffYK82JVvgl1K?startTime=1689699725000"
                rel="noopener noreferrer"
                aria-describedby="landscape_training_july_2023"
              >
                Landscape Training - July 2023
              </VideoDownloadLink>
              <MetaList id="landscape_training_july_2023">
                {renderZoomPassword(zoomPasswords?.landscape_training_july_2023)}
                <span className="meta-item">Zoom Video Link</span>
                <span className="meta-item">
                  <span
                    className="cb-glyph cb-educator"
                    data-alt="For Educators"
                    aria-label="For Educators"
                    aria-hidden="true"
                  ></span>
                </span>
              </MetaList>
            </ResourceItem>
            <br></br>
            <div>
              <h5 className="cb-roboto-slab-bold cb-margin-bottom-8">
                Getting Started with Landscape
              </h5>
              College Board hosted webinar on how to create a College Board EPL account, activate
              Landscape access, and successfully upload files.
              <div>
                <ResourceItem>
                  <VideoDownloadLink
                    className="cb-link-default cb-link-blue cb-font-weight-bold"
                    target="_blank"
                    href="https://collegeboard.zoom.us/rec/share/os1lLJj-9FpLY6P053juQYQ8JsfuX6a82yQar_cEn00Wyw7S4bvgEK1CpxqLibFy"
                    rel="noopener noreferrer"
                    aria-describedby="landscape_epl_account_creation"
                  >
                    Getting Started 1 - EPL Account Creation and Landscape Access{' '}
                  </VideoDownloadLink>
                  <MetaList id="landscape_epl_account_creation">
                    {renderZoomPassword(zoomPasswords?.landscape_epl_account_creation)}
                    <span className="meta-item">Zoom Video Link</span>
                    <span className="meta-item">
                      <span
                        className="cb-glyph cb-educator"
                        data-alt="For Educators"
                        aria-label="For Educators"
                        aria-hidden="true"
                      ></span>
                    </span>
                  </MetaList>
                </ResourceItem>
                <ResourceItem>
                  <VideoDownloadLink
                    className="cb-link-default cb-link-blue cb-font-weight-bold"
                    target="_blank"
                    href="https://collegeboard.zoom.us/rec/share/pohaMPYkqAwN1QD-TduVkvmvhHEL0-EXetYUzUzjDnHw7oyORf-ayrEQoTftnEeL.De2zMOIr4p98tOnv"
                    rel="noopener noreferrer"
                    aria-describedby="landscape_file_upload_download"
                  >
                    Getting Started 2 - Landscape File Upload, Download and Resources
                  </VideoDownloadLink>
                  <MetaList id="landscape_file_upload_download">
                    {renderZoomPassword(zoomPasswords?.landscape_file_upload_download)}
                    <span className="meta-item">Zoom Video Link</span>
                    <span className="meta-item">
                      <span
                        className="cb-glyph cb-educator"
                        data-alt="For Educators"
                        aria-label="For Educators"
                        aria-hidden="true"
                      ></span>
                    </span>
                  </MetaList>
                </ResourceItem>
                <ResourceItem>
                  <VideoDownloadLink
                    className="cb-link-default cb-link-blue cb-font-weight-bold"
                    target="_blank"
                    href="https://collegeboard.zoom.us/rec/share/v6_8f2KAEyQWsDemS8VfGivKTk9aGG1deFfXxYIm1_ySiOrzxC6gQ6SJ799iPFHz.o0OlTeZQtEaXGVcQ"
                    rel="noopener noreferrer"
                    aria-describedby="landscape_uploading_decision_files"
                  >
                    Uploading Your Decision Files
                  </VideoDownloadLink>
                  <MetaList id="landscape_uploading_decision_files">
                    {renderZoomPassword(zoomPasswords?.landscape_uploading_decision_files)}
                    <span className="meta-item">Zoom Video Link</span>
                    <span className="meta-item">
                      <span
                        className="cb-glyph cb-educator"
                        data-alt="For Educators"
                        aria-label="For Educators"
                        aria-hidden="true"
                      ></span>
                    </span>
                  </MetaList>
                </ResourceItem>
              </div>
            </div>

            <br></br>
            <div>
              <h5 className="cb-roboto-slab-bold cb-margin-bottom-8">Past Webinars</h5>
              <div>
                <ResourceItem>
                  <VideoDownloadLink
                    className="cb-link-default cb-link-blue cb-font-weight-bold"
                    target="_blank"
                    href="https://collegeboard.zoom.us/rec/share/k1yfJ04zoH3KzoaEn6aQeNwevyNmpLYuIZF-QzUAUhWpOCdBTfLd0pYiZgr4E8c.61ZFeun_goqGr9Hb"
                    rel="noopener noreferrer"
                    aria-describedby="landscape_past_webinars"
                  >
                    Webinar: Using the SAT and Landscape Data for Retention and Academic Advising on
                    Campus
                  </VideoDownloadLink>
                  <MetaList id="landscape_past_webinars">
                    {renderZoomPassword(zoomPasswords?.landscape_past_webinars)}
                    <span className="meta-item">Zoom Video Link</span>
                    <span className="meta-item">
                      <span
                        className="cb-glyph cb-educator"
                        data-alt="For Educators"
                        aria-label="For Educators"
                        aria-hidden="true"
                      ></span>
                    </span>
                  </MetaList>
                </ResourceItem>
                <ResourceItem>
                  <VideoDownloadLink
                    className="cb-link-default cb-link-blue cb-font-weight-bold"
                    target="_blank"
                    href="https://collegeboard.zoom.us/rec/share/kE3gB18_4oFXQEdf5tmH1y2SZvAihA9B5Uv3XKEWETUOFixQ72jAdpEGhop_fQY.guS7fQsIwGnazlwM"
                    rel="noopener noreferrer"
                    aria-describedby="landscape_implementation_determining_use_strategy"
                  >
                    Webinar: Guide to Landscape Implementation & Determining Strategy of Use
                  </VideoDownloadLink>
                  <MetaList id="landscape_implementation_determining_use_strategy">
                    {renderZoomPassword(
                      zoomPasswords?.landscape_implementation_determining_use_strategy
                    )}
                    <span className="meta-item">Zoom Video Link</span>
                    <span className="meta-item">
                      <span
                        className="cb-glyph cb-educator"
                        data-alt="For Educators"
                        aria-label="For Educators"
                        aria-hidden="true"
                      ></span>
                    </span>
                    <span className="meta-item">
                      A session covering the different steps of implementing Landscape: uploading
                      historical data, determining use, and what you need to do before you start
                      using the tool.
                    </span>
                  </MetaList>
                </ResourceItem>
                <ResourceItem>
                  <VideoDownloadLink
                    className="cb-link-default cb-link-blue cb-font-weight-bold"
                    target="_blank"
                    href="https://collegeboard.zoom.us/rec/share/no5p6xBrZKeA5PtotT6_THIyVSElTwRYA3eO_Mm5UPTK4hhFfqVVSYib-oH-EeJa.DDcAZ-PXJVjGK_sg"
                    rel="noopener noreferrer"
                    aria-describedby="determining_landscape_strategy"
                  >
                    Webinar: 2021 - Determining Landscape Strategy
                  </VideoDownloadLink>
                  <MetaList id="determining_landscape_strategy">
                    {renderZoomPassword(zoomPasswords?.determining_landscape_strategy)}
                    <span className="meta-item">Zoom Video Link</span>
                    <span className="meta-item">
                      <span
                        className="cb-glyph cb-educator"
                        data-alt="For Educators"
                        aria-label="For Educators"
                        aria-hidden="true"
                      ></span>
                    </span>
                    <span className="meta-item">
                      A session featuring 3 practitioners sharing insights on how they determined
                      strategy for Landscape use at their institutions.
                    </span>
                  </MetaList>
                </ResourceItem>
                {/* <ResourceItem>
                  <VideoDownloadLink
                    className="cb-link-default cb-link-blue cb-font-weight-bold"
                    target="_blank"
                    href="https://collegeboard.zoom.us/rec/share/JAsofqYEO3KoGXJTKuKU5ZAZkDaWsZkT2PRNC7ZqX_F-2NSLcC0MIYPeh8hJJDCt.3phNUfaEqqItdxaZ"
                    rel="noopener noreferrer"
                    aria-describedby="landscape_2021_22_enhancements"
                  >
                    Webinar: Sneak Peek at the 2021-22 Landscape Enhancements
                  </VideoDownloadLink>
                  <MetaList id="landscape_sneak_peak_2021_22_enhancements">
                    <span className="meta-item cb-red1-color" style={{ textTransform: 'none' }}>
                      Password To View Video - {`1e!?0Q.C?z`}
                    </span>
                    <span className="meta-item">Zoom Video Link</span>
                    <span className="meta-item">
                      <span
                        className="cb-glyph cb-educator"
                        data-alt="For Educators"
                        aria-label="For Educators"
                        aria-hidden="true"
                      ></span>
                    </span>
                    <span className="meta-item">
                      A short session covering enhancements to Landscape for the 2021-22 admissions cycle.
                    </span>
                  </MetaList>
                </ResourceItem> */}
                <ResourceItem>
                  <VideoDownloadLink
                    className="cb-link-default cb-link-blue cb-font-weight-bold"
                    target="_blank"
                    href="https://collegeboard.zoom.us/rec/share/Gc_lpNaECSzyAX3ceGI0jaWyaY4CynN6cK9PI8roaORYzpBlMG7EsR9B2LEZueYa.Zn9kTyBHV0NTNVTB"
                    rel="noopener noreferrer"
                    aria-describedby="admission_leaders_discuss_landscape_for_scholarship_review"
                  >
                    Webinar: Admissions Leaders Discuss Landscape for Scholarship Review - 8/16/21
                  </VideoDownloadLink>
                  <MetaList id="admission_leaders_discuss_landscape_for_scholarship_review">
                    {renderZoomPassword(
                      zoomPasswords?.admission_leaders_discuss_landscape_for_scholarship_review
                    )}
                    <span className="meta-item">Zoom Video Link</span>
                    <span className="meta-item">
                      <span
                        className="cb-glyph cb-educator"
                        data-alt="For Educators"
                        aria-label="For Educators"
                        aria-hidden="true"
                      ></span>
                    </span>
                  </MetaList>
                </ResourceItem>
                <ResourceItem>
                  <VideoDownloadLink
                    className="cb-link-default cb-link-blue cb-font-weight-bold"
                    target="_blank"
                    href="https://collegeboard.zoom.us/rec/share/6VBsfgn5YL6nv4jTIOmpGb2D107kf22kjIKlXLksvrcJX879Ui8ju1CoSAARX6il.7jiyUFq6c5nNpDRl?startTime=1664388423000"
                    rel="noopener noreferrer"
                    aria-describedby="jumpstart_your_use_of_landscape"
                  >
                    Webinar: Jumpstart Your Use of Landscape
                  </VideoDownloadLink>
                  <MetaList id="jumpstart_your_use_of_landscape">
                    {renderZoomPassword(zoomPasswords?.jumpstart_your_use_of_landscape)}
                    <span className="meta-item">Zoom Video Link</span>
                    <span className="meta-item">
                      <span
                        className="cb-glyph cb-educator"
                        data-alt="For Educators"
                        aria-label="For Educators"
                        aria-hidden="true"
                      ></span>
                    </span>
                    <span className="meta-item">
                      Join current Landscape users as they shed light on how to start using
                      Landscape, decide use strategy, and best practices.
                    </span>
                  </MetaList>
                </ResourceItem>
              </div>
            </div>
          </div>
        </InfoSection>
      </div>
    </>
  )
}

export { Help }
